import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Box, Typography } from "@mui/material";
import { fixedPercentage, truncateNumber } from "../../utils/common";

// Register Chart.js components with error handling
try {
  ChartJS.register(ChartDataLabels); // Register the plugin
  ChartJS.register(ArcElement, Tooltip, Legend);
} catch (error) {
  console.warn("Chart.js registration warning:", error);
}

const SMALL_SLICE_PCT = 6;

const getSlicePercent = (context) => {
  const raw = context?.chart?.data?.datasets?.[0]?.data;
  if (!Array.isArray(raw)) return 0;

  const total = raw.reduce((acc, val) => acc + (Number(val) || 0), 0);
  if (!total) return 0;

  const value = Number(raw[context.dataIndex]) || 0;
  return (value / total) * 100;
};

const getSliceColor = (datasetItem, index) => {
  const color = datasetItem?.backgroundColor;
  if (Array.isArray(color)) return color[index] || "#000";
  return color || "#000";
};

const createSmallSliceLeaderLinePlugin = (formatPercent) => ({
  id: "smallSliceLeaderLine",
  afterDatasetsDraw(chart) {
    const datasetItem = chart.data.datasets?.[0];
    const values = datasetItem?.data;
    if (!Array.isArray(values) || values.length === 0) return;

    const total = values.reduce((acc, val) => acc + (Number(val) || 0), 0);
    if (!total) return;

    const meta = chart.getDatasetMeta(0);
    const { ctx } = chart;

    ctx.save();
    values.forEach((rawValue, index) => {
      const value = Number(rawValue) || 0;
      const percent = (value / total) * 100;
      if (value <= 0 || percent >= SMALL_SLICE_PCT) return;

      const arc = meta.data[index];
      if (!arc) return;

      const { x, y, startAngle, endAngle, outerRadius } = arc.getProps(
        ["x", "y", "startAngle", "endAngle", "outerRadius"],
        true,
      );
      const angle = (startAngle + endAngle) / 2;
      const direction = Math.cos(angle) >= 0 ? 1 : -1;
      const startX = x + Math.cos(angle) * (outerRadius + 1);
      const startY = y + Math.sin(angle) * (outerRadius + 1);
      const bendX = x + Math.cos(angle) * (outerRadius + 12);
      const bendY = y + Math.sin(angle) * (outerRadius + 12);
      const endX = bendX + direction * 14;

      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(bendX, bendY);
      ctx.lineTo(endX, bendY);
      ctx.strokeStyle = getSliceColor(datasetItem, index);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();

      const text = formatPercent(percent);
      ctx.fillStyle = "#000";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = direction === 1 ? "left" : "right";
      ctx.textBaseline = "bottom";
      ctx.fillText(text, endX + direction * 4, bendY + 4);
    });
    ctx.restore();
  },
});

const PieChart = ({
  labels,
  dataset,
  customWidth,
  customHeight,
  hide,
  formatDisplayValue,
  displayLegend = false,
}) => {
  const data = {
    labels: labels ? labels : ["Red", "Blue", "#eb7e0e"],
    datasets: dataset
      ? dataset
      : [
          {
            data: [0, 0],
            backgroundColor: ["#FF4C4C", "#007BFF", "#eb7e0e"],
            borderColor: "#ffffff",
            borderWidth: 2,
            offset: [0, 20],
          },
        ],
  };

  const fmtPct = (value) =>
    typeof formatDisplayValue === "function"
      ? formatDisplayValue(value, 2)
      : truncateNumber(value, 2);

  const formatPercent = (percentage) =>
    `${fmtPct(fixedPercentage(percentage))}%`;

  const smallSliceLeaderLinePlugin =
    createSmallSliceLeaderLinePlugin(formatPercent);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 34, right: 36, bottom: 18, left: 36 },
    },
    plugins: {
      legend: {
        display: displayLegend ? true : false,
        position: "bottom",
        font: {
          size: 12,
          weight: "500",
        },
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 10,
          boxHeight: 10,
          padding: 16,
        },
      },
      tooltip: {
        titleColor: "white",
        bodyColor: "white",
      },
      datalabels: {
        clip: false,
        font: {
          weight: "bold",
          size: 11,
        },
        formatter: (value, context) => {
          const total = context.chart.data.datasets[0].data.reduce(
            (acc, val) => acc + (Number(val) || 0),
            0,
          );
          if (!total) return "0%";

          const percentage = fixedPercentage((Number(value) / total) * 100);
          return `${fmtPct(percentage)}%`;
        },
        display: (context) => {
          const value = Number(context.dataset.data[context.dataIndex]) || 0;
          return value > 0 && getSlicePercent(context) >= SMALL_SLICE_PCT;
        },
        anchor: "center",
        align: "center",
        offset: 0,
        color: "white",
      },
    },
  };

  return (
    <div
      style={{
        width: customWidth ? customWidth : 200,
        height: customHeight ? customHeight : 200,
        marginBottom: "0.5rem",
      }}
    >
      {labels?.length > 0 && dataset?.length > 0 ? (
        <Pie
          data={data}
          options={options}
          plugins={[smallSliceLeaderLinePlugin]}
        />
      ) : (
        !hide && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center", // horizontally center
              alignItems: "center", // vertically center
              height: customHeight || "18rem", // ensure it takes up space
              width: customWidth || "100%", // optional, to match parent
            }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontFamily: "inter",
                color: "#d3d3d3",
                fontWeight: "bold",
              }}
            >
              No Data Found
            </Typography>
          </Box>
        )
      )}
    </div>
  );
};

export default PieChart;
